import { useCallback, useRef } from 'react';

// Options for the long press hook
interface LongPressOptions {
    ms?: number;
    moveThreshold?: number;
}

/**
 * A custom React hook to detect a long press gesture on an element.
 * It supports both mouse and touch events.
 *
 * @param callback The function to execute when a long press is detected.
 * @param options Configuration for the long press detection.
 * @param options.ms The duration in milliseconds to wait before considering it a long press. Defaults to 500.
 * @param options.moveThreshold The maximum distance in pixels the user can move before canceling the press. Defaults to 10.
 * @returns An object of event handlers to spread onto the target component.
 */
export const useLongPress = (
    callback: (event: React.TouchEvent | React.MouseEvent) => void,
    { ms = 500, moveThreshold = 10 }: LongPressOptions = {}
) => {
    const timeout = useRef<NodeJS.Timeout>();
    const startPos = useRef<{ x: number; y: number } | null>(null);
    const hasMovedTooFar = useRef(false);

    const start = useCallback(
        (event: React.TouchEvent | React.MouseEvent) => {
            hasMovedTooFar.current = false;

            if ('touches' in event) {
                startPos.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
            } else {
                startPos.current = { x: event.clientX, y: event.clientY };
            }

            timeout.current = setTimeout(() => {
                if (!hasMovedTooFar.current) {
                    callback(event);
                }
            }, ms);
        },
        [callback, ms]
    );

    const clear = useCallback(() => {
        timeout.current && clearTimeout(timeout.current);
    }, []);

    const handleMove = useCallback(
        (event: React.TouchEvent | React.MouseEvent) => {
            if (timeout.current && startPos.current) {
                let currentPos = { x: 0, y: 0 };
                if ('touches' in event) {
                    currentPos = { x: event.touches[0].clientX, y: event.touches[0].clientY };
                } else {
                    currentPos = { x: event.clientX, y: event.clientY };
                }

                const dx = Math.abs(currentPos.x - startPos.current.x);
                const dy = Math.abs(currentPos.y - startPos.current.y);

                if (dx > moveThreshold || dy > moveThreshold) {
                    hasMovedTooFar.current = true;
                    clear();
                }
            }
        },
        [clear, moveThreshold]
    );

    return {
        onMouseDown: start,
        onTouchStart: start,
        onMouseUp: clear,
        onMouseLeave: clear,
        onTouchEnd: clear,
        onTouchMove: handleMove,
        onMouseMove: handleMove,
    };
};